import { useEffect } from "react";
import { injectIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { getGameById } from "../api/game";
import { useMedia } from "../contexts/MediaContext";
import { setGame, setGroup } from "../redux/actions";
import { getPlayablePages } from "../components/EscapeRoomRenderer";
import { ROUTES } from "../utils/constants";
import { clearItem, getGroupTable } from "../utils/helpers";
import { clearErPlaySession, loadErPlaySession } from "../utils/erPlaySessionStorage";
import { groupLinkedGameId } from "../utils/resetPlaySession";
import store from "../redux/store";

const loadingScreenStyle = {
	minHeight: "100vh",
	display: "grid",
	placeItems: "center",
	padding: "24px 16px",
	background:
		"radial-gradient(900px 420px at 50% 0%, rgba(109,40,217,0.18) 0%, rgba(255,255,255,0) 60%), linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
};

function LoadingScreen({ intl }) {
	return (
		<div style={loadingScreenStyle}>
			<div style={{ fontSize: 15, fontWeight: 700, color: "#64748b" }}>
				{intl.formatMessage({ id: "COMMON.LOADING" })}
			</div>
		</div>
	);
}

function HomeWithGameLink({ validGameId, registerSearch, intl }) {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { unlockAllMedia } = useMedia();

	useEffect(() => {
		void unlockAllMedia();
	}, [unlockAllMedia]);

	useEffect(() => {
		let cancelled = false;
		const run = async () => {
			const storedTable = getGroupTable();
			if (!storedTable) clearItem("total");

			const res = await getGameById(validGameId).catch(() => null);
			const game = res?.status === 200 ? res.data : null;
			if (cancelled) return;

			if (!game) {
				navigate({ pathname: ROUTES.ENTER_CODE }, { replace: true });
				return;
			}

			if (!game.active) {
				navigate({ pathname: ROUTES.ENTER_CODE }, { replace: true });
				return;
			}

			const session = loadErPlaySession();
			const sameSessionGame = session?.gameId && String(session.gameId) === String(game._id);
			const prevGroupGameId = groupLinkedGameId(store.getState()?.group);
			const groupBelongsToOtherGame = prevGroupGameId && String(prevGroupGameId) !== String(game._id);
			if (!sameSessionGame) {
				clearErPlaySession();
			}
			if (groupBelongsToOtherGame || !sameSessionGame) {
				dispatch(setGroup(null));
				clearItem("groupTable");
				clearItem("total");
				try {
					localStorage.removeItem("erRegisterReturnIndex");
					localStorage.removeItem("erForceRegisterFlow");
				} catch (_) {}
			}

			dispatch(setGame(game));
			const pages = getPlayablePages(game?.escapeRoom?.pages || []);
			const hasEscapeRoom = !!game?.escapeRoom;
			const hasPages = pages.length > 0;
			navigate(
				{
					pathname: hasEscapeRoom && hasPages ? ROUTES.ESCAPE_ROOM : ROUTES.REGISTER,
					search: registerSearch || undefined,
				},
				{ replace: true }
			);
		};
		run();
		return () => {
			cancelled = true;
		};
	}, [dispatch, navigate, validGameId, registerSearch]);

	return <LoadingScreen intl={intl} />;
}

function Home({ intl }) {
	const location = useLocation();

	const gameIdParam = new URLSearchParams(location.search).get("game");
	const validGameId =
		gameIdParam && /^[a-fA-F0-9]{24}$/.test(String(gameIdParam).trim())
			? String(gameIdParam).trim()
			: null;

	if (!validGameId) {
		return <Navigate to={ROUTES.ENTER_CODE} replace />;
	}

	const registerSearch = `?game=${encodeURIComponent(validGameId)}`;
	return (
		<HomeWithGameLink
			validGameId={validGameId}
			registerSearch={registerSearch}
			intl={intl}
		/>
	);
}

export default injectIntl(Home);
